import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Input,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  useSidebar,
} from '@sea-snag/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  Activity,
  BarChart3,
  Bell,
  ChevronsUpDown,
  Globe,
  Layers,
  LayoutDashboard,
  Plug,
  Plus,
  Settings,
  TrendingUp,
  UserCheck,
  Users,
} from 'lucide-react';
import * as React from 'react';
import { useMemo, useState } from 'react';

import { useOrganization } from '../contexts/organization-context';
import { useAdminCompanySearch } from '../hooks/use-admin';
import { useUser } from '../hooks/use-auth';
import { useOrgParam } from '../hooks/use-org-param';
import { notificationsApi } from '../lib/api-client';
import { filterNavSections } from '../lib/nav-visibility';
import { canSeeAdminFeatures } from '../lib/org-role';
import { queryKeys, STALE_TIMES } from '../lib/query-keys';
import {
  addRecentAdminCompany,
  clearOrganization,
  getRecentAdminCompanies,
} from '../stores/org-store';
import { NavMain, type NavSection } from './nav-main';
import { NavUser } from './nav-user';
import { SeaSnagLogo } from './sea-snag-logo';

export function AppSidebar({ ...props }: React.ComponentProps<typeof Sidebar>) {
  const orgPid = useOrgParam();
  const { organization, organizations } = useOrganization();
  const { isMobile } = useSidebar();
  const { user } = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isSuperAdmin = user?.isSuperAdmin ?? false;
  const [adminSearch, setAdminSearch] = useState('');
  const { data: searchResults } = useAdminCompanySearch(adminSearch);
  const recentCompanies = isSuperAdmin ? getRecentAdminCompanies() : [];

  const { data: unreadData } = useQuery({
    queryKey: queryKeys.notifications.unreadCount(orgPid),
    queryFn: () => notificationsApi.unreadCount(),
    enabled: !!orgPid,
    staleTime: STALE_TIMES.SHORT,
  });

  const unreadCount = unreadData?.count ?? 0;

  const prefix = `/${orgPid}`;
  const canSeeAdmin = canSeeAdminFeatures({ role: organization?.role, isSuperAdmin });

  const navSections = useMemo<NavSection[]>(
    () => [
      {
        label: 'Platform',
        items: [
          { title: 'Dashboard', url: `${prefix}/dashboard`, icon: LayoutDashboard },
          { title: 'Market Explorer', url: `${prefix}/market-explorer`, icon: Globe },
        ],
      },
      {
        label: 'Monitoring',
        items: [
          { title: 'Competitor Sets', url: `${prefix}/competitor-sets`, icon: Layers },
          { title: 'Price Alerts', url: `${prefix}/price-alerts`, icon: Bell },
          { title: 'Activity', url: `${prefix}/activity`, icon: Activity, badge: unreadCount },
        ],
      },
      {
        label: 'Pricing',
        items: [
          {
            title: 'Dynamic Pricing',
            url: `${prefix}/dynamic-pricing`,
            icon: TrendingUp,
            disabled: true,
            badgeText: 'Soon',
          },
          {
            title: 'Analytics',
            url: `${prefix}/analytics`,
            icon: BarChart3,
            adminOnly: true,
          },
        ],
      },
      {
        label: 'Settings',
        // Integrations hold API credentials, Members changes who has access and
        // Owners hands out magic links. Owners and admins only.
        adminOnly: true,
        items: [
          { title: 'Integrations', url: `${prefix}/integrations`, icon: Plug },
          { title: 'Members', url: `${prefix}/members`, icon: Users },
          { title: 'Owners', url: `${prefix}/owners`, icon: UserCheck },
        ],
      },
    ],
    [prefix, unreadCount]
  );

  const visibleSections = useMemo(
    () => filterNavSections(navSections, canSeeAdmin),
    [navSections, canSeeAdmin]
  );

  return (
    <Sidebar collapsible="icon" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            {organizations.length > 1 || isSuperAdmin ? (
              <DropdownMenu
                onOpenChange={(open) => {
                  if (!open) setAdminSearch('');
                }}
              >
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    size="lg"
                    className="group/logo data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                  >
                    <div className="group flex aspect-square size-8 items-center justify-center rounded-lg text-foreground">
                      <SeaSnagLogo className="size-5" />
                    </div>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-medium">
                        {organization?.name ?? 'Select org'}
                      </span>
                      <span className="truncate text-xs">Sea Snag</span>
                    </div>
                    <ChevronsUpDown className="ml-auto" />
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  className="w-(--radix-dropdown-menu-trigger-width) min-w-56 rounded-lg"
                  align="start"
                  side={isMobile ? 'bottom' : 'right'}
                  sideOffset={4}
                >
                  {/* Super Admin: Platform Home */}
                  {isSuperAdmin && (
                    <>
                      <DropdownMenuItem
                        className="gap-2 p-2"
                        onClick={() => {
                          clearOrganization();
                          void navigate({ to: '/admin/dashboard' });
                        }}
                      >
                        <div className="flex size-6 items-center justify-center rounded-md border bg-primary/10">
                          <Settings className="size-4 text-primary" />
                        </div>
                        <span className="font-medium">Platform Admin Home</span>
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                    </>
                  )}

                  {/* Super Admin: Search */}
                  {isSuperAdmin && (
                    <>
                      <div
                        className="px-2 py-1.5"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Input
                          placeholder="Search all companies..."
                          value={adminSearch}
                          onChange={(e) => setAdminSearch(e.target.value)}
                          className="h-8 text-xs"
                        />
                      </div>
                      {adminSearch.length >= 2 && searchResults && searchResults.length > 0 && (
                        <>
                          {searchResults.map((result) => (
                            <DropdownMenuItem
                              key={result.pid}
                              className="gap-2 p-2"
                              onClick={() => {
                                addRecentAdminCompany(result);
                                void queryClient.invalidateQueries();
                                void navigate({
                                  to: '/$orgPid/dashboard',
                                  params: { orgPid: result.pid },
                                });
                              }}
                            >
                              <div className="flex size-6 items-center justify-center rounded-md border">
                                <SeaSnagLogo className="size-4 shrink-0" color />
                              </div>
                              {result.name}
                            </DropdownMenuItem>
                          ))}
                          <DropdownMenuSeparator />
                        </>
                      )}
                    </>
                  )}

                  {/* Super Admin: Recent Companies */}
                  {isSuperAdmin && recentCompanies.length > 0 && !adminSearch && (
                    <>
                      <DropdownMenuLabel className="text-muted-foreground text-xs">
                        Recent
                      </DropdownMenuLabel>
                      {recentCompanies.map((company) => (
                        <DropdownMenuItem
                          key={company.pid}
                          className="gap-2 p-2"
                          onClick={() => {
                            addRecentAdminCompany(company);
                            void queryClient.invalidateQueries();
                            void navigate({
                              to: '/$orgPid/dashboard',
                              params: { orgPid: company.pid },
                            });
                          }}
                        >
                          <div className="flex size-6 items-center justify-center rounded-md border">
                            <SeaSnagLogo className="size-4 shrink-0" color />
                          </div>
                          {company.name}
                        </DropdownMenuItem>
                      ))}
                      <DropdownMenuSeparator />
                    </>
                  )}

                  {/* Standard org list */}
                  {organizations.length > 0 && (
                    <>
                      <DropdownMenuLabel className="text-muted-foreground text-xs">
                        {isSuperAdmin ? 'My Organizations' : 'Organizations'}
                      </DropdownMenuLabel>
                      {organizations.map((org) => (
                        <DropdownMenuItem
                          key={org.pid}
                          onClick={() => {
                            void queryClient.invalidateQueries();
                            void navigate({
                              to: '/$orgPid/dashboard',
                              params: { orgPid: org.pid },
                            });
                          }}
                          className="gap-2 p-2"
                        >
                          <div className="flex size-6 items-center justify-center rounded-md border">
                            <SeaSnagLogo className="size-4 shrink-0" color />
                          </div>
                          {org.name}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="gap-2 p-2">
                    <div className="flex size-6 items-center justify-center rounded-md border bg-transparent">
                      <Plus className="size-4" />
                    </div>
                    <div className="text-muted-foreground font-medium">Add organization</div>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <SidebarMenuButton size="lg" className="group/logo">
                <div className="group flex aspect-square size-8 items-center justify-center rounded-lg text-foreground">
                  <SeaSnagLogo className="size-5" />
                </div>
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{organization?.name ?? 'Sea Snag'}</span>
                  <span className="truncate text-xs">Sea Snag</span>
                </div>
              </SidebarMenuButton>
            )}
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>
      <SidebarContent>
        <NavMain sections={visibleSections} />
      </SidebarContent>
      <SidebarFooter>
        <NavUser />
      </SidebarFooter>
      <SidebarRail />
    </Sidebar>
  );
}
